import { useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import {
  BrandingProvider,
  LeaderboardList,
  LeaderboardRow,
  LeaderboardShell,
  formatLapTime,
  useOrientation,
  leaderboardCarLabels,
} from "@vms/leaderboard-ui";
import type { BrandingTemplate } from "@vms/types";
import {
  usePublicLeaderboardFetcher,
  usePublicLeaderboardConfig,
  usePublicLeaderboardEntries,
  useProactiveRenewal,
} from "../../api/public-leaderboard";
import { useBrandingTemplatePublic } from "../../api/branding-templates-public";

/**
 * Token-gated public leaderboard display. Loaded by display-agent webviews at
 * `/embed/leaderboard/:id?token=…`. The page reads the token from the query
 * string, forwards it on every API call, and renews transparently — reactive
 * on 401, proactive when expiry is within 24 h.
 *
 * Polling-only (10 s interval). No WS subscription — public-WS realtime is a
 * follow-up.
 */

const ENTRIES_POLL_MS = 10_000;

export function PublicLeaderboardDisplay(): JSX.Element {
  const { leaderboardId = "" } = useParams<{ leaderboardId: string }>();
  const [searchParams] = useSearchParams();
  const [token, setToken] = useState<string>(() => searchParams.get("token") ?? "");
  const [expired, setExpired] = useState(false);
  const { isLandscape } = useOrientation();

  const fetcher = usePublicLeaderboardFetcher({ leaderboardId, token, setToken, setExpired });
  const { data: config } = usePublicLeaderboardConfig(leaderboardId, fetcher);
  const { data: entries } = usePublicLeaderboardEntries(leaderboardId, fetcher, ENTRIES_POLL_MS);
  const carLabels = leaderboardCarLabels(entries);
  // Branding is now token-gated via /saved/:id/branding so the embed view
  // gets the same logo / background / position colours as the auth'd
  // /display/leaderboard/:id page.
  const { data: branding } = useBrandingTemplatePublic(
    config ? leaderboardId : null,
    token,
  );

  useProactiveRenewal(leaderboardId, config?.expiresAt, token, setToken);

  const title = config?.name ?? "Leaderboard";
  const subtitle = config?.subheading ?? undefined;

  // Compute fastest sectors across the visible entries so the renderer can
  // highlight purple/green splits. Mirrors `LeaderboardDisplay`.
  const fastestSectors = useMemo(() => {
    let s1: number | null = null;
    let s2: number | null = null;
    let s3: number | null = null;
    for (const e of entries) {
      if (e.bestLapSector1Ms != null && (s1 == null || e.bestLapSector1Ms < s1)) {
        s1 = e.bestLapSector1Ms;
      }
      if (e.bestLapSector2Ms != null && (s2 == null || e.bestLapSector2Ms < s2)) {
        s2 = e.bestLapSector2Ms;
      }
      if (e.bestLapSector3Ms != null && (s3 == null || e.bestLapSector3Ms < s3)) {
        s3 = e.bestLapSector3Ms;
      }
    }
    return { sector1Ms: s1, sector2Ms: s2, sector3Ms: s3 };
  }, [entries]);

  if (!token) {
    return <ExpiredState title="Missing access token" detail="The display URL must include `?token=…`. Ask the operator to push again." />;
  }
  if (expired) {
    return <ExpiredState title="Session expired" detail="The display-access token has expired. Ask the operator to push this leaderboard again." />;
  }

  return (
    <BrandingProvider branding={branding}>
      <LeaderboardShell title={title} subtitle={subtitle} mode="venue">
        {entries.length === 0 ? (
          <div className="flex items-center justify-center h-full text-gray-500 text-lg">
            {config ? "No lap times recorded yet." : "Loading…"}
          </div>
        ) : (
          <LeaderboardList
            totalCount={entries.length}
            renderEntry={(index) => {
              const entry = entries[index];
              return (
                <LeaderboardRow
                  position={entry.position}
                  driverName={entry.driverName}
                  car={carLabels[index]}
                  lapTime={formatLapTime(entry.bestLapTimeMs)}
                  sectors={{
                    s1: entry.bestLapSector1Ms,
                    s2: entry.bestLapSector2Ms,
                    s3: entry.bestLapSector3Ms,
                  }}
                  fastestSectors={fastestSectors}
                  showSectors={isLandscape}
                  extraColumns={[
                    { label: "Track", value: entry.trackName ?? entry.track },
                  ]}
                />
              );
            }}
          />
        )}
      </LeaderboardShell>
    </BrandingProvider>
  );
}

function ExpiredState({ title, detail }: { title: string; detail: string }): JSX.Element {
  return (
    <div className="min-h-screen flex flex-col items-center justify-center text-center p-12 bg-black text-white">
      <div className="text-3xl font-semibold mb-3">{title}</div>
      <div className="max-w-2xl text-gray-300">{detail}</div>
    </div>
  );
}

// Re-export the branding type so the hook below stays self-contained.
export type { BrandingTemplate };
